import { computed, toValue } from 'vue';
import { useQuery } from '@tanstack/vue-query';

import { queryKeys } from '@/queries/_shared/queryKeys';
import { noCacheHeaders, noCacheQueryOptions } from '@/queries/_shared/noCache';
import http from '@/services/http';

function unwrapBody(data) {
  if (data && typeof data === 'object' && 'body' in data) return data.body;
  return data;
}

function resolvePupilId(inputPupilId) {
  const direct = Number(inputPupilId || 0);
  if (Number.isFinite(direct) && direct > 0) return direct;

  const fromPidStorage = Number(localStorage.getItem('p_id') || 0);
  if (Number.isFinite(fromPidStorage) && fromPidStorage > 0) return fromPidStorage;

  const currentChildUin = String(localStorage.getItem('currentChildUin') || '').trim();
  if (currentChildUin) {
    try {
      const childList = JSON.parse(localStorage.getItem('childList') || '[]');
      if (Array.isArray(childList)) {
        const selected = childList.find((child) => String(child?.uin || '') === currentChildUin);
        const fromChild = Number(selected?.pupil_id || selected?._data?.pupil_id || 0);
        if (Number.isFinite(fromChild) && fromChild > 0) return fromChild;
      }
    } catch {
      // ignore storage parse errors
    }
  }

  try {
    const person = JSON.parse(localStorage.getItem('person') || '{}');
    const fromProfile = Number(person?.profile?.pupil_id || person?._data?.pupil_id || 0);
    if (Number.isFinite(fromProfile) && fromProfile > 0) return fromProfile;
  } catch {
    // ignore storage parse errors
  }

  return 0;
}

async function fetchAssignments(groupId) {
  const response = await http.get('/assignments', {
    headers: noCacheHeaders,
    params: {
      group_id: Number(groupId || 0),
      expand: 'teacher,subject,maLessons',
      'per-page': 1000
    }
  });
  return unwrapBody(response.data) || [];
}

async function fetchGradesMarks(startTs, endTs, pupilId) {
  const params = {
    start_ts: Number(startTs || 0),
    end_ts: Number(endTs || 0),
    expand: 'lplan,parent_response',
    'per-page': 1000
  };
  const resolvedPupilId = resolvePupilId(pupilId);
  if (resolvedPupilId > 0) params.p_id = resolvedPupilId;

  const response = await http.get('/marks/current', { params });
  return unwrapBody(response.data) || [];
}

async function fetchGradesAverage(quarter, pupilId) {
  const params = { q_num: Number(quarter || 0) };
  const resolvedPupilId = resolvePupilId(pupilId);
  if (resolvedPupilId > 0) params.p_id = resolvedPupilId;

  const response = await http.get('/marks/avg', { headers: noCacheHeaders, params });
  return unwrapBody(response.data) || [];
}

export function useGradesAssignmentsQuery(groupId, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.gradesRating.assignments(toValue(groupId))),
    queryFn: () => fetchAssignments(toValue(groupId)),
    enabled: computed(() => Number(toValue(groupId) || 0) > 0),
    ...options,
    ...noCacheQueryOptions
  });
}

export function useGradesMarksQuery(startTs, endTs, pupilId, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.gradesRating.marks(toValue(startTs), toValue(endTs), toValue(pupilId))),
    queryFn: () => fetchGradesMarks(toValue(startTs), toValue(endTs), toValue(pupilId)),
    enabled: computed(() => Number(toValue(startTs) || 0) > 0 && Number(toValue(endTs) || 0) > 0),
    ...options
  });
}

export function useGradesAverageQuery(quarter, pupilId, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.gradesRating.average(toValue(quarter), toValue(pupilId))),
    queryFn: () => fetchGradesAverage(toValue(quarter), toValue(pupilId)),
    enabled: computed(() => Number(toValue(quarter) || 0) > 0),
    ...options,
    ...noCacheQueryOptions
  });
}

export { fetchAssignments, fetchGradesAverage, fetchGradesMarks };
